/**
 * PowerOfOneSimulator Component
 * Interactive 7-lever what-if model from Scaling Up seminar.
 * All calculations run client-side using snapshot baseline.
 * Clearly marked as SIMULATION to distinguish from actuals.
 */

import { useState, useMemo, useCallback } from 'react';
import { CommentButton } from './CommentButton';
import { formatCurrency } from '@/utils/formatters';
import type { PowerOfOneBaseline } from '@/types/scalingUp';

interface PowerOfOneSimulatorProps {
  baseline: PowerOfOneBaseline;
}

interface LeverConfig {
  id: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  group: 'profit' | 'cash';
}

const LEVERS: LeverConfig[] = [
  { id: 'price', label: 'Price Change', unit: '%', min: -10, max: 20, step: 0.5, defaultValue: 0, group: 'profit' },
  { id: 'volume', label: 'Volume Change', unit: '%', min: -10, max: 20, step: 0.5, defaultValue: 0, group: 'profit' },
  { id: 'cogs', label: 'COGS Reduction', unit: '%', min: -10, max: 10, step: 0.5, defaultValue: 0, group: 'profit' },
  { id: 'overheads', label: 'Overhead Reduction', unit: '%', min: -10, max: 10, step: 0.5, defaultValue: 0, group: 'profit' },
  { id: 'arDays', label: 'AR Days Change', unit: 'days', min: -30, max: 30, step: 1, defaultValue: 0, group: 'cash' },
  { id: 'wipDays', label: 'WIP Days Change', unit: 'days', min: -21, max: 14, step: 1, defaultValue: 0, group: 'cash' },
  { id: 'apDays', label: 'AP Days Change', unit: 'days', min: -30, max: 30, step: 1, defaultValue: 0, group: 'cash' },
];

function calculateImpact(
  baseline: PowerOfOneBaseline,
  levers: Record<string, number>
): { newProfit: number; profitChange: number; cashFreed: number } {
  const allZero = Object.values(levers).every((v) => v === 0);
  if (allZero) {
    return {
      newProfit: baseline.annualisedNetProfit,
      profitChange: 0,
      cashFreed: 0,
    };
  }

  const priceFactor = 1 + (levers.price ?? 0) / 100;
  const volumeFactor = 1 + (levers.volume ?? 0) / 100;
  const cogsFactor = 1 - (levers.cogs ?? 0) / 100;
  const overheadFactor = 1 - (levers.overheads ?? 0) / 100;

  const newRevenue = baseline.annualisedRevenue * priceFactor * volumeFactor;
  const newCogs = baseline.annualisedCogs * volumeFactor * cogsFactor;
  const newOverheads = baseline.annualisedOverheads * overheadFactor;
  const newProfit = newRevenue - newCogs - baseline.annualisedDirectLabour - newOverheads;
  const profitChange = newProfit - baseline.annualisedNetProfit;

  const arChange = -(levers.arDays ?? 0) * baseline.dailyRevenue;
  const wipChange = -(levers.wipDays ?? 0) * baseline.dailyRevenue;
  const apChange = (levers.apDays ?? 0) * baseline.dailyRevenue;
  const cashFreed = arChange + wipChange + apChange;

  return {
    newProfit: Math.round(newProfit),
    profitChange: Math.round(profitChange),
    cashFreed: Math.round(cashFreed),
  };
}

export function PowerOfOneSimulator({ baseline }: PowerOfOneSimulatorProps) {
  const [leverValues, setLeverValues] = useState<Record<string, number>>(
    () => Object.fromEntries(LEVERS.map((l) => [l.id, l.defaultValue]))
  );

  const handleChange = useCallback((id: string, value: number) => {
    setLeverValues((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleReset = useCallback(() => {
    setLeverValues(
      Object.fromEntries(LEVERS.map((l) => [l.id, l.defaultValue]))
    );
  }, []);

  const impact = useMemo(
    () => calculateImpact(baseline, leverValues),
    [baseline, leverValues]
  );

  const hasChanges = Object.values(leverValues).some((v) => v !== 0);

  return (
    <div className="scaling-up__simulator" role="region" aria-label="Power of One Simulator">
      <div className="simulator__banner">
        <span className="simulator__banner-icon">&#9888;</span>
        <span className="simulator__banner-text">
          SIMULATION &mdash; These are &ldquo;what-if&rdquo; projections, not actual Xero data
        </span>
        {hasChanges && (
          <button onClick={handleReset} className="simulator__reset">
            Reset to Actuals
          </button>
        )}
      </div>

      <div className="simulator__content">
        <div className="simulator__levers">
          <div className="simulator__title-row">
            <h2 className="simulator__title">Power of One</h2>
            <CommentButton targetType="metric" targetId="power-of-one" />
          </div>
          <p className="simulator__desc">
            Adjust levers to see impact on profit and cash
          </p>

          <h3 className="simulator__group-title">Profit Levers</h3>
          {LEVERS.filter((l) => l.group === 'profit').map((lever) => (
            <LeverSlider
              key={lever.id}
              lever={lever}
              value={leverValues[lever.id] ?? 0}
              onChange={handleChange}
            />
          ))}

          <h3 className="simulator__group-title">Cash Levers</h3>
          {LEVERS.filter((l) => l.group === 'cash').map((lever) => (
            <LeverSlider
              key={lever.id}
              lever={lever}
              value={leverValues[lever.id] ?? 0}
              onChange={handleChange}
            />
          ))}
        </div>

        <div className="simulator__results">
          <h3 className="simulator__results-title">Projected Impact</h3>
          <div className="simulator__result-card">
            <span className="simulator__result-label">Annual Profit</span>
            <span className="simulator__result-value">
              {formatCurrency(impact.newProfit)}
            </span>
            <span className={`simulator__result-change ${impact.profitChange >= 0 ? 'simulator__result-change--positive' : 'simulator__result-change--negative'}`}>
              {impact.profitChange >= 0 ? '+' : ''}{formatCurrency(impact.profitChange)}
            </span>
          </div>
          <div className="simulator__result-card">
            <span className="simulator__result-label">Cash Freed</span>
            <span className="simulator__result-value">
              {formatCurrency(impact.cashFreed)}
            </span>
            <span className="simulator__result-hint">
              One-time improvement from CCC compression
            </span>
          </div>
          <div className="simulator__baseline">
            <h4>Current Actuals (Annualised)</h4>
            <div className="simulator__baseline-row">
              <span>Revenue</span>
              <span>{formatCurrency(baseline.annualisedRevenue)}</span>
            </div>
            <div className="simulator__baseline-row">
              <span>Net Profit</span>
              <span>{formatCurrency(baseline.annualisedNetProfit)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeverSlider({
  lever,
  value,
  onChange,
}: {
  lever: LeverConfig;
  value: number;
  onChange: (id: string, value: number) => void;
}) {
  return (
    <div className="lever">
      <div className="lever__header">
        <label htmlFor={`lever-${lever.id}`} className="lever__label">
          {lever.label}
        </label>
        <span className="lever__value">
          {value >= 0 ? '+' : ''}{value}{lever.unit}
        </span>
      </div>
      <input
        id={`lever-${lever.id}`}
        type="range"
        min={lever.min}
        max={lever.max}
        step={lever.step}
        value={value}
        onChange={(e) => onChange(lever.id, parseFloat(e.target.value))}
        className="lever__slider"
        aria-label={`${lever.label}: ${value}${lever.unit}`}
      />
    </div>
  );
}
